<script>
	const _ = require('underscore');
	const page = require('page');
	const { assetUrl } = require('utilities/urlUtilities');

	module.exports = {
		props: {
			assignments: {
				required: true
			},
			filterStatus: {
				required: true
			},
			onOptionClick: {
				type: Function,
				default: function () {}
			},
			isLoading: {
				type: Boolean,
				default: false
			},
			cvaAssignment: {
				required: false
			},
			skiAssignment: {
				required: false
			},
			clazz: {
				required: true
			}
		},
		components: {
			'article-result-content': require('components/article-result-content.vue').default,
			'ebook-icon': require('components/svg/ebook-icon.vue').default,
			'camera-icon': require('components/svg/camera-icon.vue').default,
			'dropdown-button': require('components/dropdown-button.vue').default
		},
		modelData: ['studentGroups'],
		computed: {
			groupsById() {
				let studentGroups = this.studentGroups || [];
				let clazz = this.clazz;
				let students = clazz.roster;
				let studentsById = students.reduce((acc, s) => {
					acc[s] = true;
					return acc;
				}, {});

				return studentGroups.reduce((acc, g) => {
					let suids = Object.keys(g.suids || {});
					let count = 0;
					suids.forEach(suid => {
						if (studentsById[suid]) {
							count++;
						}
					});
					acc[g.id] = _.assign({}, g, {
						count
					});
					return acc;
				}, {});
			},
			assignmentsList() {
				let list = [];
				let assignments = this.assignments;

				if (assignments && assignments.length) {
					list = assignments.map(a =>
						_.assign({}, a, {
							optionIcons: this.getAssignmentOptionIcons(a)
						})
					);
				}
				return list;
			}
		},
		methods: {
			visitAssignmentTab: function (assignment, tab) {
				page(this.getAssignmentTabUrl(assignment, tab));
			},
			getAssignmentTabUrl: function (assignment, tab) {
				const classCode = 'classCode:' + assignment.classCode;
				const assignmentListTab = 'assignmentListTab:' + this.filterStatus;
				const hash = `#!assignmentTab:assignments/${assignmentListTab}/${classCode}`;

				const assignmentsBackUrl = `/assignments${hash}`;

				return '/assignment/' + assignment.classCode + '/' + assignment.assignmentId + '/' + tab + '?abu=' + encodeURIComponent(assignmentsBackUrl);
			},
			getAssignmentCount(assignment) {
				let clazz = this.clazz;
				let count = (clazz && clazz.studentCount) || 0;

				if (assignment.studentUids && assignment.studentUids.length) {
					count = assignment.studentUids.length;
				}

				return count;
			},
			getStudentCount(assignment) {
				let productType = assignment && assignment.productMetaData ? assignment.productMetaData.productType : null;
				let count = 0;

				if (productType === 'AAD') {
					if (assignment.suidsInProgress) {
						count = Object.keys(assignment.suidsInProgress).length;
					}
				} else if (assignment.suidsSubmitted) {
					count = Object.keys(assignment.suidsSubmitted).length;
				} else {
					count = assignment.studentsSubmitted;
				}
				return count;
			},
			getAssignmentOptionIcons(assignment = {}) {
				let assignmentOptions = assignment && assignment.options;
				let options = [];

				if (assignmentOptions) {
					if (assignment.hasQuestionSets()) {
						options.push({
							text: 'Q',
							tooltip: 'Question Set'
						});
					}
					if (assignment.hasVocabSupport()) {
						options.push({
							text: 'VS',
							tooltip: 'Vocabulary Support'
						});
					}
					if (assignment.hasVocabActivity()) {
						options.push({
							text: 'V',
							tooltip: 'Vocabulary Activity'
						});
					}
					if (assignmentOptions.assignStepReads && assignmentOptions.assignStepReads.length) {
						options.push({
							text: 'SR',
							tooltip: 'StepRead'
						});
					}
					if (assignmentOptions.assignAudio) {
						options.push({
							type: 'audio',
							tooltip: 'Article Audio'
						});
					}
					if (assignmentOptions.assignEBook) {
						options.push({
							type: 'ebook',
							tooltip: 'eBook'
						});
					}
					if (assignmentOptions.video) {
						options.push({
							type: 'video',
							tooltip: 'Video'
						});
					}
				}
				if (assignment.gcid) {
					options.push({
						type: 'google',
						tooltip: 'Published to Google Classroom'
					});
				}

				if (assignment.gca) {
					options.push({
						type: 'google',
						tooltip: 'Published with Google Classroom Add-ons',
						class: 'gca'
					});
				}

				return options;
			},
			getSupplementalText(assignment) {
				const { challengeArticles, supportArticles, decodables } = assignment.options;

				let supplementalText;

				if (challengeArticles) {
					if (supportArticles) {
						supplementalText = 'Includes challenge and boost articles';
					} else {
						supplementalText = 'Includes challenge articles';
					}
				} else if (supportArticles) {
					supplementalText = 'Includes boost articles';
				}

				if (decodables) {
					if (supplementalText) {
						supplementalText += ' and decodables';
					} else {
						supplementalText = 'Includes decodables';
					}
				}

				return supplementalText;
			},
			assetUrl(url) {
				return assetUrl(url);
			}
		}
	};
</script>
<style lang="scss">
	@import 'config/colors';
	@import 'mixins/flexbox';

	.assignment-articles-list.articles-list {
		.assignment-article {
			display: flex;
			border-bottom: 1px solid $c-grey-light;

			> * {
				@include flex(1);
			}

			> .article-result-content {
				@include flex(2);
				margin: 0rem;
			}

			.supplemental-text {
				font-size: 0.875rem;
				margin-bottom: 0.5rem;
			}

			.assignment-options {
				display: inline-flex;

				.dropdown-button .dropdown-menu-container .dropdown-menu {
					min-width: 14.5rem;
				}
			}
		}

		.article-classification {
			> h4 {
				display: inline;
			}

			> .assigned-to {
				display: inline;
			}

			> time.article-postdate {
				display: block;
			}
		}

		.assignment-icons > span {
			position: relative;
		}

		.ebook-icon,
		.camera-icon {
			stroke: #838a8e;
			fill: #838a8e;
		}
	}
</style>
<template>
	<div class="assignment-articles-list articles-list">
		<template v-if="assignments && assignments.length === 0 && !isLoading && !cvaAssignment && !skiAssignment"> (None) </template>
		<section
			class="assignment-article"
			v-if="clazz"
			v-for="assignment in assignmentsList"
			:key="assignment.assignmentId">
			<article-result-content
				:article="assignment.productMetaData"
				:product-link="getAssignmentTabUrl(assignment, 'assessmentarticle')"
				:show-summary="false">
				<div
					slot="footer-tools"
					class="assignment-actions">
					<div
						class="supplemental-text"
						v-if="getSupplementalText(assignment)">
						{{ getSupplementalText(assignment) }}
					</div>
					<a
						v-if="clazz.studentCount === 0 && clazz.rosterType == 'student'"
						:href="`/class-admin#!classAdmin:invite/classCode:${assignment.classCode}`"
						class="button">
						Invite Students to Join Class
					</a>
					<a
						v-if="clazz.studentCount === 0 && clazz.rosterType == 'teacher'"
						:href="`/class-admin#!classAdmin:add/classCode:${assignment.classCode}`"
						class="button">
						Add Students to Class
					</a>
				</div>
			</article-result-content>
			<div class="article-classification">
				<span
					class="assigned-to"
					v-if="assignment.group">
					{{ (groupsById[assignment.group] && groupsById[assignment.group].n) || 'Specific Students' }}
				</span>
				<span
					class="assigned-to"
					v-if="!assignment.group && assignment.studentUids && assignment.studentUids.length && (!assignment.type || assignment.type === 's')"
					>Specific Students</span
				>
				<span
					class="assigned-to"
					v-if="!assignment.group && (!assignment.studentUids || !assignment.studentUids.length || assignment.type === 'w')"
					>Whole Class</span
				>
				<time class="article-postdate"> Start: {{ (assignment.startDate || assignment.creationTimestamp) | date }}</time>
				<time
					v-if="assignment.deadline"
					class="article-postdate">
					{{ assignment.productMetaData.productType === 'AAD' ? 'End Date' : 'Due Date' }}: {{ assignment.deadline | date }}
				</time>
				<div class="assignment-icons">
					<template v-for="(icon, iconIndex) in assignment.optionIcons">
						<button
							class="button -link"
							v-tooltip="icon.tooltip">
							<img
								v-if="icon.type === 'audio'"
								class="small-icon"
								alt="Audio Assigned"
								:src="assetUrl('/assets/dist/images/audio_muted_icon.svg')" />
							<ebook-icon v-if="icon.type === 'ebook'"></ebook-icon>
							<camera-icon v-if="icon.type === 'video'"></camera-icon>
							<img
								v-if="icon.type === 'google'"
								class="small-icon google"
								:class="icon.class"
								:alt="icon.tooltip"
								:src="assetUrl('/assets/dist/images/google-classroom/gc_square_32.png')" />
							{{ icon.text || '' }}{{ iconIndex !== assignment.optionIcons.length - 1 ? ',' : '' }}
						</button>
					</template>
				</div>
			</div>
			<div class="article-actions form">
				<h5 v-if="assignment.productMetaData.productType === 'AAD'">
					<template v-if="!(assignment.status === 'i' && assignment.deadline) && assignment.suidsInProgress">
						{{ getStudentCount(assignment) + ' of ' + getAssignmentCount(assignment) }}
						<small>In Progress</small>
					</template>
					<template v-if="assignment.status === 'i'">
						<small>Ended</small>
					</template>
				</h5>
				<h5 v-if="assignment.productMetaData.productType !== 'AAD'">
					<template v-if="!assignment.closed">
						{{ getStudentCount(assignment) + ' of ' + getAssignmentCount(assignment) }}
						<small>Submitted</small>
					</template>
					<template v-if="assignment.closed">
						<small>Ended</small>
					</template>
				</h5>
				<div class="assignment-options">
					<dropdown-button
						slot="actions"
						@click-item="onOptionClick"
						:disabled="isLoading"
						:items="assignment.actions"
						:data-item="assignment"
						:icon-cls="'cog fa-lg fa-solid'"></dropdown-button>
				</div>
			</div>
		</section>
		<!-- end .article -->
	</div>
	<!-- end .articles-list -->
</template>
